import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../fx/Reveal';
import TiltCard from '../fx/TiltCard';
import Icon, { ICON_NAMES } from '../common/Icon';
import { ALL_SERVICES, STACK_GROUPS, ENGAGEMENT_MODELS } from '../../data/siteContent';
import { useProducts, useTestimonials } from '../../hooks/useContent';
import { safeUrl, STATUS_LABELS } from '../../services/contentApi';

/* Reusable, conversion-focused content blocks (Home · Services · Technology). */

function Head({ label, title, sub, id }) {
  return (
    <Reveal as="header" className="qx-head">
      <span className="section-label">{label}</span>
      <h2 className="section-title" id={id}>{title}</h2>
      {sub && <p className="section-subtitle">{sub}</p>}
    </Reveal>
  );
}

export function ServicesGrid({
  label = 'Our Services',
  title = 'Everything You Need to Build, Launch & Scale',
  sub = 'From first idea to global scale — one team for web, mobile, cloud, data and AI. Tell us the outcome you want; we will bring the right people and technology.',
}) {
  return (
    <section className="section qx-section" aria-labelledby="all-services-heading">
      <div className="container">
        <Head label={label} title={title} sub={sub} id="all-services-heading" />
        <div className="qx-services">
          {ALL_SERVICES.map(({ icon, title: t, body, to }, i) => (
            <Reveal key={t} delay={(i % 4) * 70}>
              <TiltCard as="div" className="qx-service" max={6}>
                <span className="qx-service__icon"><Icon name={icon} size={24} /></span>
                <h3 className="qx-service__title">{t}</h3>
                <p className="qx-service__body">{body}</p>
                <Link to={to} className="qx-service__link" aria-label={`Learn more: ${t}`}>
                  Learn more <span aria-hidden="true">→</span>
                </Link>
              </TiltCard>
            </Reveal>
          ))}
        </div>
        <Reveal className="qx-note">
          Have something different in mind? <Link to="/contact">Tell us what you need</Link> — if it can be built, we can build it.
        </Reveal>
      </div>
    </section>
  );
}

export function StackMatrix({
  label = 'Technology',
  title = 'The Right Technology for Your Product — Not the Other Way Around',
  sub = 'We are technology-agnostic. Our engineers are fluent across modern stacks, so we recommend what fits your goals, team and budget — and we work in your preferred stack if you have one.',
}) {
  return (
    <section className="section section--alt qx-section" aria-labelledby="stack-matrix-heading">
      <div className="container">
        <Head label={label} title={title} sub={sub} id="stack-matrix-heading" />
        <div className="qx-stack">
          {STACK_GROUPS.map(({ name, items }, i) => (
            <Reveal key={name} delay={(i % 3) * 80}>
              <div className="qx-stack__group">
                <h3 className="qx-stack__name">{name}</h3>
                <ul className="qx-stack__chips">
                  {items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="qx-note">
          Don&apos;t see your stack? <Link to="/contact">Ask us</Link> — we adopt new technologies quickly.
        </Reveal>
      </div>
    </section>
  );
}

export function EngagementModels({
  label = 'Ways to Work With Us',
  title = 'Flexible Engagement Models That Fit Your Stage',
  sub = 'Whether you need a quick MVP, a dedicated squad or a few senior engineers next week — choose the model that matches your budget and timeline.',
}) {
  return (
    <section className="section qx-section" aria-labelledby="engagement-heading">
      <div className="container">
        <Head label={label} title={title} sub={sub} id="engagement-heading" />
        <div className="qx-models">
          {ENGAGEMENT_MODELS.map(({ icon, title: t, body, points }, i) => (
            <Reveal key={t} delay={i * 80}>
              <TiltCard as="div" className="qx-model" max={6}>
                <span className="qx-service__icon"><Icon name={icon} size={24} /></span>
                <h3 className="qx-service__title">{t}</h3>
                <p className="qx-service__body">{body}</p>
                <ul className="qx-model__points">
                  {points.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ProductsShowcase({
  label = 'Our Products',
  title = 'Our Own Product Line — Built by the Same Team',
  sub = 'Alongside client engagements, we are building our own software products. Interested in early access, a pilot, or a version tailored to your business? Let us know.',
  cta = true,
}) {
  const { products } = useProducts();
  if (!products.length) return null;

  return (
    <section className="section section--alt qx-section" aria-labelledby="products-showcase-heading">
      <div className="container">
        <Head label={label} title={title} sub={sub} id="products-showcase-heading" />
        <div className="qx-products">
          {products.map((p, i) => {
            const demo = safeUrl(p.demoUrl);
            const video = safeUrl(p.videoUrl);
            const image = safeUrl(p.imageUrl);
            const live = p.status === 'LIVE';
            return (
              <Reveal key={p.slug || p.id || p.name} delay={(i % 3) * 90}>
                <TiltCard as="div" className="qx-product" max={6}>
                  {image && <img className="qx-product__img" src={image} alt={`${p.name} screenshot`} loading="lazy" />}
                  <div className="qx-product__top">
                    <span className="qx-service__icon" aria-hidden="true">{ICON_NAMES.includes(p.icon) ? <Icon name={p.icon} size={24} /> : (p.icon || <Icon name="package" size={24} />)}</span>
                    <span className={`qx-product__badge qx-product__badge--${(p.status || 'PLANNED').toLowerCase()}`}>
                      {STATUS_LABELS[p.status] || 'Product'}
                    </span>
                  </div>
                  <h3 className="qx-service__title">{p.name}</h3>
                  {p.domain && <p className="qx-product__domain">{p.domain}</p>}
                  {p.tagline && <p className="qx-product__tag">{p.tagline}</p>}
                  {p.description && <p className="qx-service__body">{p.description}</p>}
                  {p.highlights && p.highlights.length > 0 && (
                    <ul className="qx-model__points">
                      {p.highlights.map((h) => <li key={h}>{h}</li>)}
                    </ul>
                  )}
                  {p.proofText && (
                    <blockquote className="qx-product__proof">
                      {p.proofTitle && <strong>{p.proofTitle}</strong>}
                      <span>{p.proofText}</span>
                      {p.proofSource && <cite>— {p.proofSource}</cite>}
                    </blockquote>
                  )}
                  {p.techStack && <p className="qx-product__stack">{p.techStack}</p>}
                  <div className="qx-product__actions">
                    {demo && <a href={demo} target="_blank" rel="noopener noreferrer" className="qx-product__btn">Live demo ↗</a>}
                    {video && <a href={video} target="_blank" rel="noopener noreferrer" className="qx-product__btn qx-product__btn--ghost">Watch video ↗</a>}
                    <Link to="/contact" className="qx-service__link">{live ? 'Request a demo' : 'Get early access'} <span aria-hidden="true">→</span></Link>
                  </div>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
        {cta && (
          <Reveal className="qx-note">
            <Link to="/products">See all products</Link> · Want something built for your exact needs? <Link to="/contact">Tell us your requirements</Link>.
          </Reveal>
        )}
      </div>
    </section>
  );
}

/** Client feedback / proof — renders nothing until the admin publishes at least one item. */
export function TestimonialsBlock({
  label = 'Client Feedback',
  title = 'What Our Clients Say',
}) {
  const items = useTestimonials();
  if (!items.length) return null;
  return (
    <section className="section qx-section" aria-labelledby="testimonials-heading">
      <div className="container">
        <Head label={label} title={title} id="testimonials-heading" />
        <div className="qx-quotes">
          {items.map((t, i) => {
            const logo = safeUrl(t.logoUrl);
            const src = safeUrl(t.sourceUrl);
            return (
              <Reveal key={t.id} delay={(i % 3) * 80}>
                <figure className="qx-quote">
                  <blockquote>{t.quote}</blockquote>
                  <figcaption>
                    {logo && <img src={logo} alt="" loading="lazy" />}
                    <span>
                      <strong>{t.name}</strong>
                      <em>{[t.role, t.company].filter(Boolean).join(', ')}</em>
                    </span>
                    {src && <a href={src} target="_blank" rel="noopener noreferrer" aria-label="View source">↗</a>}
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
